import { Dialog, Transition } from '@headlessui/react';
import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { getAuthorizationHeader, http } from '../../config/http';
import { toast } from 'react-toastify';
import {
  IconBuildingStore,
  IconX,
  IconAlertCircle,
  IconArrowRight,
  IconCheck,
  IconLoader2,
  IconChartPie,
  IconListDetails,
  IconPhoto,
  IconSearch,
  IconInbox,
} from '@tabler/icons-react';
import RingkasanKategoriKarat from './Summary/RingkasanKategoriKarat';
import { resolveItemImageUrl } from '../../utils/itemImage';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

export default function ModalTransferAntarToko({
  isOpen,
  onClose,
  selectedItems = [],
  setSelectedItems,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [loadingCabang, setLoadingCabang] = useState(false);
  const [cabangTujuanList, setCabangTujuanList] = useState([]);
  const [cabangTujuanId, setCabangTujuanId] = useState('');
  const [catatanPemohon, setCatatanPemohon] = useState('');
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'list' | 'galeri'
  const [searchItem, setSearchItem] = useState('');

  const userRole = localStorage.getItem('role');
  const isOperator = userRole === 'operator';
  const isApprover = ['owner', 'admin', 'verifikator'].includes(userRole);

  // Reset tab and search on open/close
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('summary');
      setSearchItem('');
      setCabangTujuanId('');
      setCatatanPemohon('');
    }
  }, [isOpen]);

  // Statistics
  const totalPcs = selectedItems.length;
  const totalBerat = selectedItems.reduce(
    (acc, curr) => acc + (parseFloat(curr?.berat) || 0),
    0
  );

  const totalKategori = useMemo(() => {
    const cats = new Set(
      selectedItems.map(
        (i) =>
          i?.jenis_kategori?.kategori?.nama_kategori ||
          i?.kategori?.nama_kategori ||
          i?.item?.jenis_kategori?.kategori?.nama_kategori ||
          'Tanpa Kategori'
      )
    );
    return cats.size;
  }, [selectedItems]);

  const avgBerat = totalPcs > 0 ? totalBerat / totalPcs : 0;

  const validModalItems = useMemo(() => {
    return selectedItems.filter(
      (it) => Number(it?.harga_modal || it?.item?.harga_modal || 0) > 0
    );
  }, [selectedItems]);

  const avgHargaModal = useMemo(() => {
    if (validModalItems.length === 0) return 0;
    const sumModal = validModalItems.reduce(
      (acc, it) => acc + Number(it?.harga_modal || it?.item?.harga_modal || 0),
      0
    );
    return Math.round(sumModal / validModalItems.length);
  }, [validModalItems]);

  const minBerat = useMemo(() => {
    const validWeights = selectedItems
      .map((it) => parseFloat(it?.berat || it?.item?.berat || 0) || 0)
      .filter((w) => w > 0);
    return validWeights.length > 0 ? Math.min(...validWeights) : 0;
  }, [selectedItems]);

  const maxBerat = useMemo(() => {
    if (selectedItems.length === 0) return 0;
    return Math.max(
      ...selectedItems.map((it) => parseFloat(it?.berat || it?.item?.berat || 0) || 0)
    );
  }, [selectedItems]);

  const minHargaModal = useMemo(() => {
    if (validModalItems.length === 0) return 0;
    return Math.min(
      ...validModalItems.map((it) => Number(it?.harga_modal || it?.item?.harga_modal || 0) || 0)
    );
  }, [validModalItems]);

  const maxHargaModal = useMemo(() => {
    if (selectedItems.length === 0) return 0;
    return Math.max(
      ...selectedItems.map((it) => Number(it?.harga_modal || it?.item?.harga_modal || 0) || 0)
    );
  }, [selectedItems]);

  // Filtered items for List & Galeri
  const filteredItems = useMemo(() => {
    if (!searchItem.trim()) return selectedItems;
    const q = searchItem.toLowerCase().trim();
    return selectedItems.filter((item) => {
      const kode = String(item.kode_item || '').toLowerCase();
      const nama = String(
        item.jenis_kategori?.nama_jenis || item.kategori?.nama_kategori || ''
      ).toLowerCase();
      const baki = String(item.nomor_rak || '').toLowerCase();
      return kode.includes(q) || nama.includes(q) || baki.includes(q);
    });
  }, [selectedItems, searchItem]);

  // Fetch branches on modal open
  useEffect(() => {
    if (!isOpen) return;

    async function fetchCabangTujuan() {
      setLoadingCabang(true);
      try {
        const res = await http.get('transfer-antar-toko/cabang-tujuan', {
          headers: { Authorization: getAuthorizationHeader() },
        });
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setCabangTujuanList(res.data.data);
        } else if (Array.isArray(res.data)) {
          setCabangTujuanList(res.data);
        }
      } catch (err) {
        console.error('Gagal mengambil daftar cabang toko tujuan:', err);
        toast.error('Gagal memuat daftar toko tujuan');
      } finally {
        setLoadingCabang(false);
      }
    }

    fetchCabangTujuan();
  }, [isOpen]);

  const handleRemoveItem = (itemId) => {
    if (setSelectedItems) {
      setSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!cabangTujuanId) {
      toast.error('Pilih toko tujuan terlebih dahulu!');
      return;
    }

    if (!selectedItems || selectedItems.length === 0) {
      toast.error('Pilih minimal 1 barang untuk dipindahkan!');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        items: selectedItems.map((item) => item.id),
        cabang_tujuan_id: parseInt(cabangTujuanId, 10),
        catatan_pemohon: catatanPemohon.trim() || null,
      };

      const res = await http.post('transfer-antar-toko', payload, {
        headers: { Authorization: getAuthorizationHeader() },
      });

      if (res.data?.status) {
        toast.success(res.data.message || 'Transfer berhasil diproses!', {
          autoClose: 2000,
        });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(res.data?.message || 'Gagal memproses transfer');
      }
    } catch (err) {
      console.error('Error transfer antar toko:', err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        'Terjadi kesalahan saat memproses transfer';
      toast.error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    } finally {
      setLoading(false);
    }
  };

  const selectedCabangObj = cabangTujuanList.find(
    (c) => String(c.id) === String(cabangTujuanId)
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[99999]"
        onClose={loading ? () => {} : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300 sm:duration-400"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300 sm:duration-400"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto w-screen max-w-full sm:max-w-2xl lg:max-w-4xl transform bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 text-left align-middle transition-all flex flex-col h-screen max-h-screen">
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between shrink-0 sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                      <IconBuildingStore size={22} />
                    </div>
                    <div>
                      <Dialog.Title
                        as="h2"
                        className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100"
                      >
                        Transfer ke Toko Lain
                      </Dialog.Title>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Total:{' '}
                        <span className="font-semibold text-slate-700 dark:text-slate-300">
                          {totalPcs} Barang | {totalBerat.toFixed(3)} gr
                        </span>
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  >
                    <IconX size={18} />
                  </button>
                </div>

                {/* Navigation Tab Bar */}
                <div className="flex border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 shrink-0 sticky top-[61px] z-20">
                  <button
                    type="button"
                    onClick={() => setActiveTab('summary')}
                    className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === 'summary'
                        ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                  >
                    <IconChartPie size={16} />
                    <span>Rangkuman Item</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('list')}
                    className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === 'list'
                        ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                  >
                    <IconListDetails size={16} />
                    <span>List Item ({totalPcs})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('galeri')}
                    className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === 'galeri'
                        ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                  >
                    <IconPhoto size={16} />
                    <span>Galeri List Item</span>
                  </button>
                </div>

                {/* Tab Content Body */}
                <div className="p-5 flex-1 bg-slate-50/50 dark:bg-slate-900/50 overflow-y-auto min-h-[350px]">
                  {/* 1. TAB RANGKUMAN ITEM */}
                  {activeTab === 'summary' && (
                    <div className="space-y-4">
                      {/* 9 Stat Cards */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-3">
                        <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">
                            Total Barang
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            {totalPcs}{' '}
                            <span className="text-xs font-semibold text-slate-500">
                              Pcs
                            </span>
                          </div>
                        </div>
                        <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">
                            Total Berat
                          </span>
                          <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                            {totalBerat.toFixed(3)}{' '}
                            <span className="text-xs font-semibold text-slate-500 font-sans">
                              gr
                            </span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                            Total Kategori
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            {totalKategori}{' '}
                            <span className="text-xs font-semibold text-slate-500">
                              Kategori
                            </span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                            Rata-rata Berat
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {avgBerat.toFixed(3)}{' '}
                            <span className="text-xs font-semibold text-slate-500 font-sans">
                              gr
                            </span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                            Berat Minimal
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {minBerat.toFixed(3)}{' '}
                            <span className="text-xs font-semibold text-slate-500 font-sans">
                              gr
                            </span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                            Berat Maksimal
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {maxBerat.toFixed(3)}{' '}
                            <span className="text-xs font-semibold text-slate-500 font-sans">
                              gr
                            </span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                            Rata-rata Modal
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            Rp {avgHargaModal.toLocaleString('id-ID')}
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                            Modal Minimal
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            Rp {minHargaModal.toLocaleString('id-ID')}
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                            Modal Maksimal
                          </span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            Rp {maxHargaModal.toLocaleString('id-ID')}
                          </div>
                        </div>
                      </div>

                      {/* Informational Banner */}
                      {isOperator ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs">
                          <IconAlertCircle
                            size={18}
                            className="flex-shrink-0 mt-0.5 text-amber-500"
                          />
                          <div>
                            <span className="font-semibold">
                              Menunggu Verifikasi:
                            </span>{' '}
                            Pengajuan transfer akan dikirim ke{' '}
                            <strong>Owner / Admin / Verifikator</strong> untuk
                            diverifikasi sebelum barang resmi dipindahkan ke toko
                            tujuan.
                          </div>
                        </div>
                      ) : isApprover ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-xs">
                          <IconCheck
                            size={18}
                            className="flex-shrink-0 mt-0.5 text-emerald-500"
                          />
                          <div>
                            <span className="font-semibold">
                              Transfer Langsung:
                            </span>{' '}
                            Sebagai <strong>{userRole.toUpperCase()}</strong>,
                            barang akan langsung dipindahkan ke toko tujuan dan
                            baki barang akan direset.
                          </div>
                        </div>
                      ) : null}

                      {/* Breakdown Kategori & Karat */}
                      <RingkasanKategoriKarat selectedItems={selectedItems} />

                      {/* Form Transfer Controls */}
                      <div className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3.5">
                        {/* Pilihan Toko Tujuan */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                            Toko Tujuan <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={cabangTujuanId}
                            onChange={(e) => setCabangTujuanId(e.target.value)}
                            disabled={loading || loadingCabang}
                            className="w-full px-3 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-slate-100 dark:disabled:bg-slate-800 transition"
                          >
                            <option value="">
                              {loadingCabang
                                ? 'Memuat daftar toko...'
                                : '-- Pilih Toko Tujuan --'}
                            </option>
                            {cabangTujuanList.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nama_cabang}{' '}
                                {c.lokasi ? `(${c.lokasi})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Catatan / Alasan */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                            Catatan / Keterangan Transfer (Opsional)
                          </label>
                          <textarea
                            rows={2}
                            value={catatanPemohon}
                            onChange={(e) => setCatatanPemohon(e.target.value)}
                            disabled={loading}
                            placeholder="Contoh: Permintaan stok dari cabang lain / pemerataan etalase..."
                            className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-amber-500 focus:outline-none text-xs resize-none"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 2. TAB LIST ITEM */}
                  {activeTab === 'list' && (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="relative flex-1 max-w-xs">
                          <IconSearch
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            size={15}
                          />
                          <input
                            type="text"
                            placeholder="Cari kode, jenis, baki..."
                            value={searchItem}
                            onChange={(e) => setSearchItem(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                          />
                        </div>
                        <span className="text-[11px] text-slate-500 font-medium">
                          Menampilkan {filteredItems.length} dari {totalPcs} item
                        </span>
                      </div>

                      <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-[380px] bg-white dark:bg-slate-900">
                        <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                          <thead className="bg-slate-50 dark:bg-slate-800/80 uppercase border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10 font-bold tracking-wider text-[11px] text-slate-500 dark:text-slate-400">
                            <tr>
                              <th className="p-2.5 w-10 text-center">No</th>
                              <th className="p-2.5">Kode Barang</th>
                              <th className="p-2.5">Nama Barang</th>
                              <th className="p-2.5 text-center">Baki / Rak</th>
                              <th className="p-2.5 text-right">Berat (gr)</th>
                              <th className="p-2.5 text-center">Karat</th>
                              <th className="p-2.5 text-center w-16">Batal</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {filteredItems.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={7}
                                  className="text-center py-8 text-slate-400"
                                >
                                  Tidak ada item yang sesuai dengan pencarian
                                </td>
                              </tr>
                            ) : (
                              filteredItems.map((item, idx) => (
                                <tr
                                  key={item.id || idx}
                                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                  <td className="p-2.5 text-slate-500 font-medium text-center">
                                    {idx + 1}.
                                  </td>
                                  <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">
                                    {item.kode_item}
                                  </td>
                                  <td className="p-2.5 font-medium text-slate-700 dark:text-slate-200">
                                    {item.jenis_kategori?.nama_jenis ||
                                      item.kategori?.nama_kategori ||
                                      '-'}
                                  </td>
                                  <td className="p-2.5 text-center font-medium">
                                    {item.nomor_rak || '-'}
                                  </td>
                                  <td className="p-2.5 text-right font-bold text-slate-800 dark:text-slate-100 font-mono">
                                    {parseFloat(item.berat || 0).toFixed(3)} gr
                                  </td>
                                  <td className="p-2.5 text-center font-semibold text-amber-600 dark:text-amber-400">
                                    {item.karat} K
                                  </td>
                                  <td className="p-2.5 text-center">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveItem(item.id)}
                                      disabled={loading || selectedItems.length <= 1}
                                      className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                      title={
                                        selectedItems.length <= 1
                                          ? 'Minimal 1 barang'
                                          : 'Batal Pilih'
                                      }
                                    >
                                      <IconX size={15} />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                          <tfoot className="bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 font-bold sticky bottom-0 z-10">
                            <tr>
                              <td
                                colSpan={4}
                                className="p-2.5 text-right text-slate-700 dark:text-slate-300"
                              >
                                Total Berat:
                              </td>
                              <td className="p-2.5 text-right text-amber-600 dark:text-amber-400 font-mono">
                                {totalBerat.toFixed(3)} gr
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 3. TAB GALERI LIST ITEM */}
                  {activeTab === 'galeri' && (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="relative flex-1 max-w-xs">
                          <IconSearch
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            size={15}
                          />
                          <input
                            type="text"
                            placeholder="Cari kode, jenis, baki..."
                            value={searchItem}
                            onChange={(e) => setSearchItem(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                          />
                        </div>
                        <span className="text-[11px] text-slate-500 font-medium">
                          Menampilkan {filteredItems.length} foto item
                        </span>
                      </div>

                      {filteredItems.length === 0 ? (
                        <div className="py-12 text-center text-slate-400">
                          <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                          <p className="text-xs">
                            Tidak ada item yang sesuai dengan pencarian
                          </p>
                        </div>
                      ) : (
                        <PhotoProvider>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[380px] overflow-y-auto pr-1">
                            {filteredItems.map((item) => {
                              const imgUrl = resolveItemImageUrl(item);
                              return (
                                <div
                                  key={item.id || item.kode_item}
                                  className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow group flex flex-col relative"
                                >
                                  <div className="aspect-square bg-slate-100 dark:bg-slate-700/50 relative flex items-center justify-center overflow-hidden">
                                    {imgUrl ? (
                                      <PhotoView src={imgUrl}>
                                        <img
                                          src={imgUrl}
                                          alt={item.kode_item}
                                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '';
                                          }}
                                        />
                                      </PhotoView>
                                    ) : (
                                      <div className="flex flex-col items-center justify-center text-slate-400 p-4 text-center">
                                        <IconPhoto
                                          size={28}
                                          className="opacity-40 mb-1"
                                        />
                                        <span className="text-[10px]">
                                          Tidak ada foto
                                        </span>
                                      </div>
                                    )}
                                    <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-xs text-white text-[10px] font-mono font-bold">
                                      {item.karat}K
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveItem(item.id)}
                                      disabled={loading || selectedItems.length <= 1}
                                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center transition opacity-80 hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                      title={
                                        selectedItems.length <= 1
                                          ? 'Minimal 1 barang'
                                          : 'Batal Pilih'
                                      }
                                    >
                                      <IconX size={12} />
                                    </button>
                                  </div>

                                  <div className="p-2.5 flex-1 flex flex-col justify-between text-xs">
                                    <div>
                                      <span className="font-mono font-bold text-amber-600 dark:text-amber-400 block truncate">
                                        {item.kode_item}
                                      </span>
                                      <span className="text-slate-600 dark:text-slate-300 font-medium block truncate text-[11px]">
                                        {item.jenis_kategori?.nama_jenis ||
                                          item.kategori?.nama_kategori ||
                                          '-'}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100 dark:border-slate-700 text-[11px]">
                                      <span className="text-slate-400">
                                        Baki: {item.nomor_rak || '-'}
                                      </span>
                                      <span className="font-bold text-slate-800 dark:text-slate-200">
                                        {parseFloat(item.berat || 0).toFixed(3)} gr
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </PhotoProvider>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 shrink-0 sticky bottom-0 z-20">
                  <div className="text-xs text-slate-500 truncate max-w-sm">
                    {selectedCabangObj ? (
                      <span>
                        Tujuan:{' '}
                        <strong className="text-amber-600 dark:text-amber-400">
                          {selectedCabangObj.nama_cabang}
                        </strong>
                      </span>
                    ) : (
                      <span className="italic text-slate-400">
                        Pilih toko tujuan untuk melanjutkan transfer
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={loading}
                      className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer"
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={
                        loading || !cabangTujuanId || selectedItems.length === 0
                      }
                      className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 active:scale-95 text-white text-xs font-bold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                    >
                      {loading ? (
                        <>
                          <IconLoader2 size={16} className="animate-spin" />
                          <span>Memproses...</span>
                        </>
                      ) : (
                        <>
                          <IconArrowRight size={16} />
                          <span>
                            {isApprover ? 'Pindah Sekarang' : 'Ajukan Transfer'}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
